/**
 * <s-announcement-rotator> — cycles through its direct child messages one at a time.
 * Children are stacked in one grid cell so the bar never changes height.
 * Rotation pauses while the bar is hovered or focused, and on reduced-motion
 * preferences messages swap without the fade/slide transition (handled in CSS).
 */
class SAnnouncementRotator extends HTMLElement {
  #timer;
  #index = 0;
  #paused = false;
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const messages = this.#messages();
    messages.forEach((el, i) => this.#setActive(el, i === 0));
    if (messages.length < 2) return;

    const pause = () => (this.#paused = true);
    const resume = () => (this.#paused = false);
    this.addEventListener('mouseenter', pause, opts);
    this.addEventListener('mouseleave', resume, opts);
    this.addEventListener('focusin', pause, opts);
    this.addEventListener('focusout', resume, opts);

    const interval = Number(this.getAttribute('interval')) || 3500;
    this.#timer = setInterval(() => this.#advance(), interval);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#ac?.abort();
  }

  #messages() {
    return Array.from(this.children);
  }

  #setActive(el, active) {
    el.toggleAttribute('data-active', active);
    el.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  #advance() {
    if (this.#paused || document.hidden) return;
    const messages = this.#messages();
    if (messages.length < 2) return;
    this.#setActive(messages[this.#index], false);
    this.#index = (this.#index + 1) % messages.length;
    this.#setActive(messages[this.#index], true);
  }
}

if (!customElements.get('s-announcement-rotator')) {
  customElements.define('s-announcement-rotator', SAnnouncementRotator);
}
